// src/features/delegations/pages/CreateDelegationPage.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addYears } from 'date-fns';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDelegations } from '../hooks/useDelegations';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import StaffMemberPicker from '../../../components/common/StaffMemberPicker';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

function FieldHelp({ tip }) {
  return (
    <Tooltip title={tip} placement="top" arrow>
      <IconButton size="small" tabIndex={-1} sx={{ color: 'text.disabled', p: 0.25, mr: -0.5 }}>
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function makeInitialForm() {
  const today = new Date();
  return {
    delegator_id: '',
    recipient_id: '',
    title: '',
    task_description: '',
    steps: '',
    competency_requirements: '',
    contraindications: '',
    supervision: '',
    issued_date: today,
    review_date: addYears(today, 1),
  };
}

export default function CreateDelegationPage() {
  const { t } = useTranslation(['delegations', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const location = useLocation();
  const { createDelegation, createRevision, loading, error, clearError } = useDelegations();
  const { showSuccess, showError } = useSnackbar();

  // A new revision of `delegationId`, or a new delegation copied from another one
  const revisionPrefill = location.state?.revisionPrefill ?? null;
  const copyPrefill = location.state?.copyPrefill ?? null;
  const sourceDelegationId = location.state?.delegationId ?? null;
  const prefill = revisionPrefill ?? copyPrefill;

  const [form, setForm] = useState(makeInitialForm);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!prefill) return;
    const today = new Date();
    setForm({
      delegator_id: prefill.delegator_id ?? '',
      recipient_id: prefill.recipient_id ?? '',
      title: prefill.title ?? '',
      task_description: prefill.task_description ?? '',
      steps: prefill.steps ?? '',
      competency_requirements: prefill.competency_requirements ?? '',
      contraindications: prefill.contraindications ?? '',
      supervision: prefill.supervision ?? '',
      issued_date: today,
      review_date: addYears(today, 1),
    });
  }, [prefill]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const setPicker = (field) => (id) => {
    setForm((prev) => ({ ...prev, [field]: id ?? '' }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const setDate = (field) => (date) => {
    setForm((prev) => ({ ...prev, [field]: date }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errors = {};
    const contentFields = [
      'title', 'task_description', 'steps',
      'competency_requirements', 'contraindications', 'supervision',
    ];
    if (!sourceDelegationId) {
      contentFields.unshift('delegator_id', 'recipient_id');
    }
    contentFields.forEach((f) => {
      if (!form[f] || !String(form[f]).trim()) errors[f] = t('common:validation.required');
    });
    if (!form.issued_date) errors.issued_date = t('common:validation.required');
    if (!form.review_date) errors.review_date = t('common:validation.required');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const dates = {
      issued_date: format(form.issued_date, 'yyyy-MM-dd'),
      review_date: format(form.review_date, 'yyyy-MM-dd'),
    };

    if (sourceDelegationId) {
      const payload = {
        title: form.title,
        task_description: form.task_description,
        steps: form.steps,
        competency_requirements: form.competency_requirements,
        contraindications: form.contraindications,
        supervision: form.supervision,
        ...dates,
      };
      const result = await createRevision(sourceDelegationId, payload);
      if (result.success) {
        showSuccess(t('delegations:create.success'));
        navigateBack(`../${sourceDelegationId}`);
      } else {
        showError(result.error);
      }
    } else {
      const payload = { ...form, ...dates };
      const result = await createDelegation(payload);
      if (result.success) {
        showSuccess(t('delegations:create.success'));
        navigate(`../${result.data?.data?.delegation_id}`);
      } else {
        showError(result.error);
      }
    }
  };

  return (
    <FormPage
      title={
        revisionPrefill ? t('delegations:create.new_revision_title')
          : copyPrefill ? t('delegations:create.copy_title')
            : t('delegations:create.title')
      }
      subtitle={
        revisionPrefill
          ? t('delegations:create.new_revision_subtitle', { title: revisionPrefill.title || t('delegations:detail.untitled') })
          : copyPrefill
            ? t('delegations:create.copy_subtitle', { title: copyPrefill.title || t('delegations:detail.untitled') })
            : t('delegations:create.subtitle')
      }
      onBack={() => navigateBack('..')}
      onSubmit={handleSubmit}
      fieldErrors={fieldErrors}
      error={error}
      onErrorClose={clearError}
    >
      {revisionPrefill && (
        <Alert severity="info">
          {t('delegations:create.new_revision_banner')}
        </Alert>
      )}
      {copyPrefill && (
        <Alert severity="info">
          {t('delegations:create.copy_banner')}
        </Alert>
      )}

      {/* Parties — hidden when creating a revision (parties are fixed on the delegation) */}
      {!sourceDelegationId && (
        <FormSection title={t('delegations:create.section.parties')}>
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ flex: '1 1 300px' }}>
            <StaffMemberPicker
              label={t('delegations:create.fields.delegator_id')}
              required
              value={form.delegator_id || null}
              onChange={setPicker('delegator_id')}
              error={!!fieldErrors.delegator_id}
              helperText={fieldErrors.delegator_id}
              excludeIds={form.recipient_id ? [form.recipient_id] : []}
              size="medium"
              sx={{ flex: 1 }}
            />
            <FieldHelp tip={t('delegations:create.tooltips.delegator_id')} />
          </Box>
          <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ flex: '1 1 300px' }}>
            <StaffMemberPicker
              label={t('delegations:create.fields.recipient_id')}
              required
              value={form.recipient_id || null}
              onChange={setPicker('recipient_id')}
              error={!!fieldErrors.recipient_id}
              helperText={fieldErrors.recipient_id}
              excludeIds={form.delegator_id ? [form.delegator_id] : []}
              size="medium"
              sx={{ flex: 1 }}
            />
            <FieldHelp tip={t('delegations:create.tooltips.recipient_id')} />
          </Box>
        </Box>
        </FormSection>
      )}

      <FormSection title={t('delegations:create.section.content')}>
        <TextField
          label={t('delegations:create.fields.title')}
          required
          value={form.title}
          onChange={set('title')}
          error={!!fieldErrors.title}
          helperText={fieldErrors.title}
          fullWidth
          inputProps={{ maxLength: 255 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <FieldHelp tip={t('delegations:create.tooltips.title')} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t('delegations:create.fields.task_description')}
          required
          value={form.task_description}
          onChange={set('task_description')}
          error={!!fieldErrors.task_description}
          helperText={fieldErrors.task_description}
          fullWidth
          multiline
          rows={3}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <FieldHelp tip={t('delegations:create.tooltips.task_description')} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t('delegations:create.fields.steps')}
          required
          value={form.steps}
          onChange={set('steps')}
          error={!!fieldErrors.steps}
          helperText={fieldErrors.steps}
          fullWidth
          multiline
          rows={4}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <FieldHelp tip={t('delegations:create.tooltips.steps')} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t('delegations:create.fields.competency_requirements')}
          required
          value={form.competency_requirements}
          onChange={set('competency_requirements')}
          error={!!fieldErrors.competency_requirements}
          helperText={fieldErrors.competency_requirements}
          fullWidth
          multiline
          rows={2}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <FieldHelp tip={t('delegations:create.tooltips.competency_requirements')} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t('delegations:create.fields.contraindications')}
          required
          value={form.contraindications}
          onChange={set('contraindications')}
          error={!!fieldErrors.contraindications}
          helperText={fieldErrors.contraindications}
          fullWidth
          multiline
          rows={2}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <FieldHelp tip={t('delegations:create.tooltips.contraindications')} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t('delegations:create.fields.supervision')}
          required
          value={form.supervision}
          onChange={set('supervision')}
          error={!!fieldErrors.supervision}
          helperText={fieldErrors.supervision}
          fullWidth
          multiline
          rows={2}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <FieldHelp tip={t('delegations:create.tooltips.supervision')} />
              </InputAdornment>
            ),
          }}
        />
      </FormSection>

      <FormSection title={t('delegations:create.section.dates')}>
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ flex: '1 1 200px' }}>
            <DatePicker
              label={t('delegations:create.fields.issued_date')}
              value={form.issued_date}
              onChange={setDate('issued_date')}
              slotProps={{
                textField: {
                  error: !!fieldErrors.issued_date,
                  required: true,
                  helperText: fieldErrors.issued_date,
                  sx: { flex: 1 },
                },
              }}
            />
            <FieldHelp tip={t('delegations:create.tooltips.issued_date')} />
          </Box>
          <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ flex: '1 1 200px' }}>
            <DatePicker
              label={t('delegations:create.fields.review_date')}
              value={form.review_date}
              onChange={setDate('review_date')}
              minDate={form.issued_date ?? undefined}
              slotProps={{
                textField: {
                  error: !!fieldErrors.review_date,
                  required: true,
                  helperText: fieldErrors.review_date,
                  sx: { flex: 1 },
                },
              }}
            />
            <FieldHelp tip={t('delegations:create.tooltips.review_date')} />
          </Box>
        </Box>
      </FormSection>

      <FormActions
        onCancel={() => navigateBack('..')}
        submitType="submit"
        submitLabel={loading ? t('common:buttons.saving') : t('delegations:create.buttons.submit')}
        loading={loading}
      />
    </FormPage>
  );
}
